import React, { useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { IoCloseCircle } from "react-icons/io5";

// Renders dialogs at the end of <body>, so an animated (transformed) parent
// can't clip a fixed-position overlay.
export const Portal = ({ children }) => ReactDOM.createPortal(<div className="hx-portal">{children}</div>, document.body);

// Keeps keyboard focus inside an open dialog, closes it on Escape and gives
// focus back to whatever opened it.
export const useDialog = (open, onClose) => {
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const opener = document.activeElement;
    ref.current?.focus();

    const onKey = (event) => {
      if (event.key === "Escape") {
        onClose();
        return;
      }
      if (event.key !== "Tab" || !ref.current) return;
      const items = ref.current.querySelectorAll(
        'button:not([disabled]), a[href], input:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      if (opener && opener.focus) opener.focus();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return ref;
};

// Bottom sheet as in the app: slides up over a dimmed page.
const Sheet = ({ open, onClose, title, tinted = false, actions, children }) => {
  const ref = useDialog(open, onClose);
  if (!open) return null;

  return (
    <Portal>
      <div className="hx-sheet-backdrop" onClick={onClose}>
        <div
          ref={ref}
          className={`hx-sheet ${tinted ? "is-tinted" : ""}`}
          role="dialog"
          aria-modal="true"
          aria-label={title}
          tabIndex={-1}
          onClick={(event) => event.stopPropagation()}
        >
          {title ? (
            <div className="hx-sheet-head">
              <h2 className="hx-sheet-title">{title}</h2>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {actions}
                <button type="button" className="hx-icon-btn hx-sheet-close" onClick={onClose} aria-label="Close">
                  <IoCloseCircle />
                </button>
              </div>
            </div>
          ) : null}
          {children}
        </div>
      </div>
    </Portal>
  );
};

export default Sheet;
